import { useEffect, useState } from "react";
import { Spin } from "antd";
import { Trans } from "react-i18next";
import { t } from "i18next";
import { useNavigate } from "react-router-dom";
import classNames from "classnames";
import { Button } from "@kalodata/kds-design";
import { IconLinePlus } from "@kalodata/kds-design-icons";
import { twMerge } from "tailwind-merge";
import Image from "@components/Image";
import TruncateTitle from "@components/TruncateTitle";
import { fetchUserProductList, type UserProductRecord } from "@apis/http/api";
import { useAppDispatch } from "@hooks/redux";
import { updateFocusType, updateProductType } from "@pages/library/librarySlice";
import type { SelectedProductPayload } from "./types";

export interface CreatedProductsPanelProps {
  onSelect: (payload: SelectedProductPayload) => void;
}

/**
 * 「Add From Library」→ Created tab：用户「我创建的」商品主体列表。
 *
 * 设计稿节点：4855:9243（有商品）、4898:9306（空态）。
 */
export const CreatedProductsPanel = ({ onSelect }: CreatedProductsPanelProps) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [list, setList] = useState<UserProductRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState<string | number | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await fetchUserProductList({ pageNo: 1, pageSize: 60 });
        if (cancelled) return;
        const data: UserProductRecord[] = Array.isArray(res?.data) ? res.data : [];
        setList(data);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleEmptyJump = () => {
    dispatch(updateFocusType("product"));
    dispatch(updateProductType("created"));
    navigate("/library");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Spin />
      </div>
    );
  }

  if (list.length === 0) {
    return (
      <div className="flex min-h-[400px] flex-col items-center justify-center gap-[16px] text-kds-fg">
        <div className="size-[110px] rounded-[12px] bg-kds-bg-element flex items-center justify-center text-[40px] text-kds-fg-soft">
          <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="10" y="14" width="22" height="26" rx="2" fill="currentColor" opacity="0.3" />
            <rect x="16" y="8" width="22" height="26" rx="2" fill="currentColor" opacity="0.45" />
          </svg>
        </div>
        <div className="text-[16px] font-medium leading-[22px] text-kds-fg text-center">
          <Trans>No Products Yet</Trans>
        </div>
        <Button
          type="reverse"
          size="lg"
          iconPlacement="start"
          icon={<IconLinePlus size={20} className="text-kds-light-reverse" />}
          className={twMerge(
            "bg-kds-dark-reverse text-kds-light-reverse shadow-[0_1px_3px_0_var(--kds-shadow)]",
            "hover:bg-(--palette-slate-11) hover:text-kds-light-reverse",
          )}
          onClick={handleEmptyJump}
        >
          <Trans>Create New Product</Trans>
        </Button>
      </div>
    );
  }

  return (
    <div className="kds-scroll-thin grid max-h-[60vh] min-h-[400px] grid-cols-4 gap-[16px] overflow-y-auto px-[16px] py-[12px]">
      {list.map((record) => {
        const isSelected = selectedId === record.id;
        return (
          <div key={record.id} className="relative">
            <div
              className="cursor-pointer overflow-hidden rounded-[8px] border border-kds-border bg-kds-bg-card transition-colors hover:border-kds-accent"
              onClick={() => {
                setSelectedId(record.id);
                onSelect({
                  origin: "library-created",
                  productId: String(record.id),
                  raw: record,
                });
              }}
            >
              <Image
                url={record.mainImage?.url}
                className="aspect-square w-full rounded-none! border-none! object-cover"
              />
              <div className="px-[10px] pb-[12px] pt-[10px]">
                <TruncateTitle
                  className="h-[36px] text-[13px] leading-[18px] text-kds-fg"
                  text={record.title ?? ""}
                  lineclamp2
                  placement="top"
                />
              </div>
            </div>
            <div
              className={classNames(
                "absolute -top-[2px] -right-[2px] -bottom-[2px] -left-[2px] z-10 rounded-[10px] border-2 transition-colors pointer-events-none",
                isSelected ? "border-kds-accent!" : "border-transparent",
              )}
              aria-hidden={!isSelected}
              aria-label={t("Selected")}
            />
          </div>
        );
      })}
    </div>
  );
};
